import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Wand2, MessageSquare, Search, User, FileText } from "lucide-react";
import AIProgressIndicator from "@/components/AIProgressIndicator";

const MODULES = [
    { code: "summary", name: "Professional Summary", feature: "H01", icon: User, hint: "2-sentence summary from your existing data." },
    { code: "biography", name: "Biography Writer", feature: "H02", icon: Wand2, hint: "Warm, factual paragraph (140-220 words)." },
    { code: "seo", name: "SEO Optimizer", feature: "H03", icon: Search, hint: "seo_title, meta_description, keywords." },
    { code: "faq", name: "FAQ Generator", feature: "H04", icon: MessageSquare, hint: "4-8 realistic FAQs, grounded only in your input." },
    { code: "article", name: "Article Generator", feature: "H05", icon: FileText, hint: "600-900 word patient-friendly article draft." },
    { code: "rewrite", name: "Content Rewriter", feature: "H06", icon: Sparkles, hint: "Tone-adjusted rewrite of your text." },
];

export default function AIStudio() {
    const [active, setActive] = useState("summary");
    const [inputs, setInputs] = useState({});
    const [jobs, setJobs] = useState([]);
    const [ent, setEnt] = useState(null);
    const [doctor, setDoctor] = useState(null);
    const [busy, setBusy] = useState(false);
    const [busyStartedAt, setBusyStartedAt] = useState(null);

    async function loadJobs() {
        try {
            const r = await api.get("/ai-studio/jobs");
            setJobs(r.data);
        } catch { /* poll error — retried on next tick */ }
    }
    useEffect(() => {
        api.get("/me/entitlements").then((r) => setEnt(r.data)).catch(() => {});
        api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {});
        loadJobs();
        const t = setInterval(loadJobs, 5000);
        return () => clearInterval(t);
    }, []);

    const activeMod = MODULES.find((m) => m.code === active);
    const unlocked = new Set((ent?.items || []).filter((i) => i.unlocked).map((i) => i.feature_code));
    const canUseActive = unlocked.has(activeMod?.feature);

    async function submit() {
        if (!canUseActive) return;
        // Client-side input validation — matches server-side gate in
        // routes_ai_studio._MODULE_REQUIRED_INPUT_KEYS. Prevents the doomed
        // enqueue-then-fail loop the user hit.
        if (active === "article" && !(inputs.topic || "").trim()) {
            toast.error("Please enter a topic before running the Article writer.");
            return;
        }
        if (active === "rewrite" && !(inputs.raw_text || "").trim()) {
            toast.error("Please paste the text you want rewritten.");
            return;
        }
        setBusy(true);
        setBusyStartedAt(Date.now());
        try {
            const body = {};
            if (active === "biography" || active === "summary") body.raw_bio_notes = inputs.notes;
            if (active === "article") body.topic = inputs.topic;
            if (active === "faq") body.raw_bio_notes = inputs.notes;
            if (active === "seo") body.raw_bio_notes = inputs.notes;
            if (active === "rewrite") { body.raw_text = inputs.raw_text; body.tone = inputs.tone || "warm"; }
            await api.post(`/ai-studio/${active}`, body);
            toast.success("Job queued");
            loadJobs();
        } catch (e) {
            // Surface the new 400 codes cleanly.
            const detail = e?.response?.data?.detail;
            if (detail && typeof detail === "object") {
                if (detail.code === "ai_studio_profile_incomplete") {
                    toast.error("Complete your profile first", { description: detail.message, duration: 8000 });
                } else if (detail.code === "ai_studio_input_missing") {
                    toast.error("Missing input", { description: detail.message, duration: 6000 });
                } else {
                    toast.error(errMsg(detail, "Failed"));
                }
            } else {
                toast.error(detail || "Failed");
            }
        } finally {
            setBusy(false);
        }
    }

    async function applyToProfile(jobId) {
        try {
            const r = await api.post(`/ai-studio/apply/${jobId}`);
            toast.success(r.data.note || "Applied");
        } catch (e) {
            toast.error(errMsg(e, "Failed to apply"));
        }
    }
    async function saveAsArticle(job) {
        const out = job.output || {};
        try {
            await api.post("/ai-studio/articles", {
                title: out.title || "Untitled",
                subtitle: out.subtitle || "",
                body_markdown: out.body_markdown || "",
                tags: out.tags || [],
                slug: out.slug || (out.title || "").toLowerCase().replace(/\s+/g, "-"),
                source_job_id: job.id,
            });
            toast.success("Saved as draft article");
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        }
    }

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="ai-studio-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Content engine</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">AI Studio</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">
                    All outputs are DRAFTS. Nothing goes live until you apply + republish your profile. Rate limit: 20 generations / day.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                {MODULES.map((m) => {
                    const Icon = m.icon;
                    const on = unlocked.has(m.feature);
                    return (
                        <button
                            key={m.code}
                            onClick={() => setActive(m.code)}
                            className={"rounded-xl border-2 p-4 text-left transition-all " + (active === m.code ? "border-[#0F766E] bg-[#0F766E]/5" : "border-[#123B5D]/10 bg-white hover:border-[#0F766E]") + (on ? "" : " opacity-60")}
                            data-testid={`module-card-${m.code}`}
                        >
                            <Icon size={20} className="text-[#0F766E] mb-2" />
                            <div className="font-bold text-[#123B5D]">{m.name}</div>
                            <div className="text-xs text-[#17212B]/60 mt-1">{m.hint}</div>
                            <div className="text-[10px] mt-2 uppercase tracking-widest">{on ? <span className="text-[#15803D]">Unlocked</span> : <span className="text-[#DC2626]">Requires {m.feature}</span>}</div>
                        </button>
                    );
                })}
            </div>

            <Card className="border-[#123B5D]/10 mb-6">
                <CardHeader><CardTitle className="text-base">{activeMod?.name}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    {!canUseActive && (
                        <div className="text-sm text-[#DC2626] bg-red-50 rounded-md p-3">
                            This module is not unlocked on your current package. Upgrade your package or complete advance payment first.
                        </div>
                    )}
                    {active === "article" ? (
                        <Input placeholder="Topic (e.g. 'How to manage seasonal allergies')" value={inputs.topic || ""} onChange={(e) => setInputs({ ...inputs, topic: e.target.value })} data-testid="ai-input-topic" />
                    ) : active === "rewrite" ? (
                        <>
                            <Input placeholder="Tone (warm, formal, patient-friendly...)" value={inputs.tone || ""} onChange={(e) => setInputs({ ...inputs, tone: e.target.value })} />
                            <Textarea placeholder="Paste the text you want rewritten" rows={5} value={inputs.raw_text || ""} onChange={(e) => setInputs({ ...inputs, raw_text: e.target.value })} data-testid="ai-input-raw_text" />
                        </>
                    ) : (
                        <Textarea placeholder="Optional extra biography notes (LLM will use profile + this)" rows={4} value={inputs.notes || ""} onChange={(e) => setInputs({ ...inputs, notes: e.target.value })} data-testid="ai-input-notes" />
                    )}
                    <Button onClick={submit} disabled={busy || !canUseActive} data-testid="ai-generate-btn">
                        {busy ? "Queuing…" : "Generate"}
                    </Button>
                    {busy && (
                        <AIProgressIndicator
                            status="processing"
                            startedAt={busyStartedAt}
                            label={`Generating ${active}`}
                            sub="Grounded on your profile — usually 15-45 seconds."
                        />
                    )}
                </CardContent>
            </Card>

            <Card className="border-[#123B5D]/10">
                <CardHeader><CardTitle className="text-base">Recent generations</CardTitle></CardHeader>
                <CardContent>
                    {jobs.length === 0 ? (
                        <div className="text-sm text-[#17212B]/60">Your generations appear here. Poll interval: 5 seconds.</div>
                    ) : (
                        <ul className="divide-y divide-[#123B5D]/10">
                            {jobs.map((j) => {
                                const module = j.source?.split(".", 2)[1] || "?";
                                return (
                                    <li key={j.id} className="py-3" data-testid={`job-${j.id}`}>
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#0F766E]/10 text-[#0F766E]">{module}</span>
                                            <span className={"text-xs px-2 py-0.5 rounded-full " + (j.status === "completed" ? "bg-green-100 text-[#15803D]" : j.status === "failed" ? "bg-red-100 text-[#DC2626]" : "bg-slate-100 text-slate-700")}>{j.status}</span>
                                            <span className="text-xs text-[#17212B]/50">{j.created_at?.slice(0, 19)}</span>
                                            <div className="ml-auto flex gap-2">
                                                {j.status === "completed" && (module === "biography" || module === "summary" || module === "seo" || module === "faq") && (
                                                    <Button size="sm" variant="outline" onClick={() => applyToProfile(j.id)} data-testid={`apply-${j.id}`}>Apply to profile</Button>
                                                )}
                                                {j.status === "completed" && module === "article" && (
                                                    <Button size="sm" variant="outline" onClick={() => saveAsArticle(j)} data-testid={`save-article-${j.id}`}>Save as article</Button>
                                                )}
                                            </div>
                                        </div>
                                        {j.output && (
                                            <pre className="text-xs bg-[#F5F8FA] rounded p-3 mt-2 whitespace-pre-wrap break-words overflow-x-auto max-h-60">
                                                {typeof j.output === "string" ? j.output : JSON.stringify(j.output, null, 2)}
                                            </pre>
                                        )}
                                        {j.error && <div className="text-xs text-[#DC2626] mt-2">{j.error}</div>}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
